import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'

// Spec §5.4/§3 — dropdown panels are positioned from the trigger's bounding box and
// rendered at the document root (layer "Dropdown overlays + click-catcher", z=4) so they're
// never clipped by a scrolling screen body. Shared by PhoneInput's dial-code list and Select.
const DropdownOverlay = ({ triggerRef, isOpen, onClose, children, align, matchWidth, placement }) => {
  const panelRef = useRef(null)
  const [rect, setRect] = useState(null)

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return undefined
    const update = () => setRect(triggerRef.current.getBoundingClientRect())
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen, triggerRef])

  useEffect(() => {
    if (!isOpen) return undefined
    const handlePointerDown = (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return
      onClose()
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose, triggerRef])

  if (!isOpen || !rect) return null

  const style = {
    position: 'fixed',
    top: placement === 'top' ? undefined : rect.bottom + 4,
    bottom: placement === 'top' ? window.innerHeight - rect.top + 4 : undefined,
    left: align === 'right' ? undefined : rect.left,
    right: align === 'right' ? window.innerWidth - rect.right : undefined,
    width: matchWidth ? rect.width : undefined,
    // Portaled to document.body, so this is a sibling of Container's .scrim (z-index 1000),
    // not a descendant inside the widget's own stacking context - var(--dd-z-dropdown) (4)
    // would be painted entirely underneath the widget. Needs to beat the scrim directly.
    zIndex: 1001,
  }

  return createPortal(
    <div ref={panelRef} style={style}>
      {children}
    </div>,
    document.body,
  )
}

DropdownOverlay.propTypes = {
  triggerRef: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  align: PropTypes.oneOf(['left', 'right']),
  matchWidth: PropTypes.bool,
  placement: PropTypes.oneOf(['top', 'bottom']),
}

DropdownOverlay.defaultProps = {
  align: 'left',
  matchWidth: true,
  placement: 'bottom',
}

export default DropdownOverlay
